import React, { useEffect, useState } from 'react';

type Req = (path: string, body?: any, signal?: AbortSignal) => Promise<any>;

const MIN = 0.1, MAX = 2.5, STEP = 0.05; // owner 2026-10-25
const fmt = (m: number) => String(Math.round(m * 100) / 100) + 'x';
const money = (n: number) => '$' + (Math.round(n * 100) / 100).toLocaleString('en-AU', { maximumFractionDigits: 2 });
const r1 = (n: number) => Math.round(n * 20) / 20;

// Owner kill switch and SEPARATE stake multipliers (singles / multis) for bets copied from Fady's paper workspace.
const FadyMirrorToggle: React.FC<{ request: Req }> = ({ request }) => {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [scope, setScope] = useState<'singles' | 'multis' | 'both'>('both');
  const [saved, setSaved] = useState({ s: 1, m: 1 });
  const [draft, setDraft] = useState({ s: 1, m: 1 });
  const [maxStake, setMaxStake] = useState<number>(1000);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const apply = (d: any) => {
    setEnabled(!!d.enabled);
    if (['singles', 'multis', 'both'].includes(d.scope)) setScope(d.scope);
    const s = typeof d.singles_multiplier === 'number' ? d.singles_multiplier : typeof d.multiplier === 'number' ? d.multiplier : 1;
    const m = typeof d.multis_multiplier === 'number' ? d.multis_multiplier : s;
    setSaved({ s, m }); setDraft({ s, m });
    if (typeof d.max_stake === 'number') setMaxStake(d.max_stake);
  };
  useEffect(() => {
    const ac = new AbortController();
    request('/api/rolling/simple-bsp/fady-mirror', undefined, ac.signal)
      .then(apply).catch((e: any) => { if (!ac.signal.aborted) setError(e.message); });
    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  async function post(body: any) {
    setBusy(true); setError('');
    try { apply(await request('/api/rolling/simple-bsp/fady-mirror', body)); }
    catch (e: any) { setError(e.message); } finally { setBusy(false); }
  }
  async function flip() {
    if (enabled === null) return;
    const next = !enabled;
    if (!window.confirm(next
      ? `Mimic Fady? Singles at ${fmt(saved.s)} and multis at ${fmt(saved.m)} his stakes will be placed LIVE from your account.`
      : 'Stop mimicking Fady? Every Fady bet not yet submitted will be withdrawn.')) return;
    await post({ enabled: next });
  }
  async function save(kind: 's' | 'm') {
    const v = draft[kind], key = kind === 's' ? 'singles_multiplier' : 'multis_multiplier';
    if (v === saved[kind]) return;
    if (v > saved[kind] && !window.confirm(`Scale Fady's ${kind === 's' ? 'singles' : 'multis'} to ${fmt(v)}? A $100 Fady bet becomes ${money(100 * v)}.`)) return;
    await post({ [key]: v });
  }
  const slider = (kind: 's' | 'm', label: string) => (
    <label className="fady-mirror-scale" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginRight: 18 }}>
      <span style={{ minWidth: 52 }}>{label}</span>
      <input type="range" min={MIN} max={MAX} step={STEP} value={draft[kind]} disabled={busy || enabled === null}
        aria-label={'Fady ' + label + ' multiplier'} onChange={e => setDraft(d => ({ ...d, [kind]: r1(Number(e.target.value)) }))} />
      <output style={{ minWidth: 40, fontWeight: 600 }}>{fmt(draft[kind])}</output>
      <span style={{ display: 'inline-flex', gap: 2 }}>{[0.5, 0.6, 0.7, 0.75, 0.8, 0.9, 1].map(v => <button type="button" key={v} disabled={busy || enabled === null} aria-pressed={draft[kind] === v} style={draft[kind] === v ? { background: '#15803d', color: '#fff' } : undefined} onClick={() => setDraft(d => ({ ...d, [kind]: v }))}>{v}</button>)}</span>
      <small>$100 → {money(100 * draft[kind])}</small>
      <button type="button" disabled={busy || enabled === null || draft[kind] === saved[kind]} onClick={() => save(kind)}>
        {draft[kind] === saved[kind] ? 'Saved' : `Apply ${fmt(draft[kind])}`}
      </button>
    </label>
  );
  return (
    <div className="fady-mirror-toggle" role="group" aria-label="Mimic Fady">
      <strong>Mimic Fady</strong>
      <span className={'fady-mirror-state ' + (enabled ? 'on' : 'off')}>{enabled === null ? '...' : enabled ? `ON - singles ${fmt(saved.s)} · multis ${fmt(saved.m)}` : 'OFF'}</span>
      <button type="button" disabled={busy || enabled === null} onClick={flip}>{enabled ? 'Stop mimicking Fady' : 'Mimic Fady'}</button>
      <span role="group" aria-label="What to copy" style={{ display: 'inline-flex', gap: 4, marginLeft: 12 }}>
        <span>Copy</span>
        {(['singles', 'multis', 'both'] as const).map(s => <button type="button" key={s} aria-pressed={scope === s} disabled={busy || enabled === null}
          style={scope === s ? { background: '#15803d', color: '#fff', borderColor: '#15803d' } : undefined}
          onClick={() => { if (s !== scope) void post({ scope: s }); }}>{s === 'singles' ? 'Singles' : s === 'multis' ? 'Multis' : 'Both'}</button>)}
      </span>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, margin: '8px 0' }}>
        {slider('s', 'Singles')}
        {slider('m', 'Multis')}
      </div>
      <small>Singles = Fady's manual bets, Multis = his parlays; each has its own scale ({fmt(MIN)}-{fmt(MAX)}). Fady paper stakes above {money(maxStake)} are not copied. His copies of bucket picks are not copied; in a race with your Brain pick his manual bet is dropped. His edits and deletions follow until 30s before the jump. A new scale re-stakes copies not yet submitted (multis not yet started are replaced).</small>
      {error && <span role="alert">{error}</span>}
    </div>
  );
};
export default FadyMirrorToggle;
